/**
 * MuniSpatialDisambiguator — CB-A · MUNI-SPATIAL
 *
 * Carga el polígono municipal desde CDAU R2 y expone
 * `check(x, y, ine5)` para rechazar candidatos geocodificados
 * fuera del municipio esperado.
 *
 * Resuelve el bug Colomera (INE 18051, Granada) vs Colomers
 * (INE 17048, Girona): CartoCiudad puede devolver un candidato
 * con nombre parecido pero municipio radicalmente distinto.
 *
 * URL CANÓNICA CDAU R2:
 *   Worker: /cdau/cdau-{ine5}.json.gz  ← prefijo «cdau-», SIN subdir
 *   ⚠️  NO usar /cdau/{ine5}.json.gz      (sin prefijo)
 *   ⚠️  NO usar /nga/{ine5}/nga-{ine5}…  (esa es NGA, con subdir)
 *
 * Penalización: −20 pp confianza cuando el candidato cae fuera.
 * Si todos están fuera → warn + continuar sin descartar (degradado).
 *
 * @module services/geocoding/spatial/MuniSpatialDisambiguator
 * @task CB-A (CLASSIF-B plan v2.0)
 * @machine M4
 * @version 1.0.0
 */

import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { point, polygon, multiPolygon } from '@turf/helpers';
import type { Feature, Polygon, MultiPolygon, BBox } from 'geojson';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * URL base del Worker R2 (alias local).
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE`).
 */
const WORKER_BASE_URL = WORKER_BASE;

/** Penalización en puntos de confianza para candidatos fuera del municipio */
export const OUTSIDE_CONFIDENCE_PENALTY = 20;

/** Máxima caché de polígonos en memoria (por INE5) */
const POLYGON_CACHE_MAX = 50;

/** TTL de caché de polígonos: 60 minutos */
const POLYGON_CACHE_TTL_MS = 60 * 60 * 1000;

// =============================================================================
// TIPOS
// =============================================================================

/** Resultado de la comprobación espacial */
export interface SpatialCheckResult {
  /** ¿El punto está dentro del polígono municipal? */
  isInside: boolean;
  /**
   * Modo de ejecución:
   * - 'polygon'  → geometría GeoJSON cargada desde CDAU R2
   * - 'bbox'     → fallback con bbox calculado de los portales
   * - 'degraded' → polígono no disponible; `isInside` siempre true
   */
  mode: 'polygon' | 'bbox' | 'degraded';
}

/** Entrada interna de caché */
interface CacheEntry {
  geometry: Feature<Polygon | MultiPolygon> | BBox | null;
  mode: 'polygon' | 'bbox' | 'degraded';
  timestamp: number;
}

/** Geometría raw que puede venir del Worker */
interface RawGeometry {
  type: 'Polygon' | 'MultiPolygon';
  coordinates: number[][][] | number[][][][];
}

// =============================================================================
// SINGLETON
// =============================================================================

let _instance: MuniSpatialDisambiguator | null = null;

export function getMuniSpatialDisambiguator(): MuniSpatialDisambiguator {
  if (!_instance) {
    _instance = new MuniSpatialDisambiguator();
  }
  return _instance;
}

export function resetMuniSpatialDisambiguator(): void {
  _instance = null;
}

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

export class MuniSpatialDisambiguator {
  private readonly baseUrl: string;
  private readonly cache = new Map<string, CacheEntry>();
  /** Promises en vuelo para evitar fetches duplicados por el mismo INE5 */
  private readonly loading = new Map<string, Promise<CacheEntry>>();

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? WORKER_BASE_URL;
  }

  // ---------------------------------------------------------------------------
  // API PÚBLICA
  // ---------------------------------------------------------------------------

  /**
   * Comprueba si (x, y) en UTM30 EPSG:25830 está dentro del municipio.
   *
   * En modo degradado siempre devuelve isInside=true para no penalizar
   * candidatos cuando el polígono no está disponible.
   */
  async check(x: number, y: number, ine5: string): Promise<SpatialCheckResult> {
    const entry = await this.getOrLoad(ine5);

    if (entry.mode === 'degraded' || entry.geometry === null) {
      return { isInside: true, mode: 'degraded' };
    }

    if (entry.mode === 'bbox') {
      const bbox = entry.geometry as BBox; // [minX, minY, maxX, maxY]
      const isInside =
        x >= bbox[0] && x <= bbox[2] && y >= bbox[1] && y <= bbox[3];
      return { isInside, mode: 'bbox' };
    }

    // mode === 'polygon'
    try {
      const pt = point([x, y]);
      const geom = entry.geometry as Feature<Polygon | MultiPolygon>;
      const isInside = booleanPointInPolygon(pt, geom);
      return { isInside, mode: 'polygon' };
    } catch (err) {
      console.warn('[MUNI-SPATIAL] Error en point-in-polygon:', err);
      return { isInside: true, mode: 'degraded' };
    }
  }

  /** Precarga el polígono para un INE5 (warm-up). check() lo llama internamente. */
  async preload(ine5: string): Promise<void> {
    await this.getOrLoad(ine5);
  }

  /** ¿El polígono está en caché y vigente para este INE5? */
  isLoaded(ine5: string): boolean {
    const entry = this.cache.get(ine5);
    if (!entry) return false;
    if (Date.now() - entry.timestamp > POLYGON_CACHE_TTL_MS) {
      this.cache.delete(ine5);
      return false;
    }
    return true;
  }

  /** Vacía la caché (útil en tests). */
  clearCache(): void {
    this.cache.clear();
  }

  get cacheSize(): number {
    return this.cache.size;
  }

  // ---------------------------------------------------------------------------
  // LÓGICA INTERNA
  // ---------------------------------------------------------------------------

  private async getOrLoad(ine5: string): Promise<CacheEntry> {
    if (!/^\d{5}$/.test(ine5)) {
      console.warn(`[MUNI-SPATIAL] INE5 inválido: "${ine5}"`);
      return this.degraded();
    }

    const cached = this.getCached(ine5);
    if (cached) return cached;

    const inflight = this.loading.get(ine5);
    if (inflight) return inflight;

    const promise = this.fetchAndStore(ine5);
    this.loading.set(ine5, promise);
    try {
      return await promise;
    } finally {
      this.loading.delete(ine5);
    }
  }

  private getCached(ine5: string): CacheEntry | null {
    const entry = this.cache.get(ine5);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > POLYGON_CACHE_TTL_MS) {
      this.cache.delete(ine5);
      return null;
    }
    return entry;
  }

  private async fetchAndStore(ine5: string): Promise<CacheEntry> {
    // URL CANÓNICA: /cdau/cdau-{ine5}.json.gz  (prefijo «cdau-», sin subdir)
    // Verificada contra CDAUR2Service.ts — NO usar /cdau/{ine5}.json.gz sin prefijo
    const url = `${this.baseUrl}/cdau/cdau-${ine5}.json.gz`;
    console.info(`[MUNI-SPATIAL] Cargando polígono INE ${ine5}: ${url}`);

    let raw: Record<string, unknown>;
    try {
      const response = await fetch(url);

      if (response.status === 404) {
        console.warn(`[MUNI-SPATIAL] Sin datos CDAU para INE ${ine5} → modo degradado`);
        return this.storeAndReturn(ine5, this.degraded());
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // Intentar parseo JSON directo; si falla, descomprimir gzip
      try {
        const clone = typeof response.clone === 'function' ? response.clone() : response;
        raw = (await clone.json()) as Record<string, unknown>;
      } catch {
        if (response.body && typeof DecompressionStream !== 'undefined') {
          const decompressed = response.body.pipeThrough(
            new DecompressionStream('gzip')
          );
          const text = await new Response(decompressed).text();
          raw = JSON.parse(text) as Record<string, unknown>;
        } else {
          throw new Error('No se pudo descomprimir respuesta gzip');
        }
      }
    } catch (err) {
      console.warn(`[MUNI-SPATIAL] Error cargando polígono INE ${ine5}:`, err);
      return this.storeAndReturn(ine5, this.degraded());
    }

    // Extraer geometría del campo `boundary`
    if (raw.boundary && this.isRawGeometry(raw.boundary)) {
      const geomRaw = raw.boundary as RawGeometry;
      try {
        const geom = this.buildTurfGeometry(geomRaw);
        if (geom) {
          console.info(
            `[MUNI-SPATIAL] Polígono cargado para INE ${ine5} (${geomRaw.type})`
          );
          return this.storeAndReturn(ine5, {
            geometry: geom,
            mode: 'polygon',
            timestamp: Date.now(),
          });
        }
      } catch (err) {
        console.warn('[MUNI-SPATIAL] Error construyendo geometría Turf:', err);
      }
    }

    // Fallback: bbox calculado de los portales
    const portales = raw.portales as Array<{ x: number; y: number }> | undefined;
    if (portales && portales.length > 0) {
      const bbox = this.computeBbox(portales);
      if (bbox) {
        console.info(
          `[MUNI-SPATIAL] Fallback bbox para INE ${ine5} (${portales.length} portales)`
        );
        return this.storeAndReturn(ine5, {
          geometry: bbox,
          mode: 'bbox',
          timestamp: Date.now(),
        });
      }
    }

    console.warn(
      `[MUNI-SPATIAL] Sin geometría utilizable para INE ${ine5} → modo degradado`
    );
    return this.storeAndReturn(ine5, this.degraded());
  }

  private storeAndReturn(ine5: string, entry: CacheEntry): CacheEntry {
    if (this.cache.size >= POLYGON_CACHE_MAX) {
      const oldest = this.cache.keys().next().value;
      if (oldest) this.cache.delete(oldest);
    }
    this.cache.set(ine5, entry);
    return entry;
  }

  private degraded(): CacheEntry {
    return { geometry: null, mode: 'degraded', timestamp: Date.now() };
  }

  // ---------------------------------------------------------------------------
  // UTILIDADES GEOMÉTRICAS
  // ---------------------------------------------------------------------------

  private isRawGeometry(value: unknown): value is RawGeometry {
    if (!value || typeof value !== 'object') return false;
    const obj = value as Record<string, unknown>;
    return (
      (obj.type === 'Polygon' || obj.type === 'MultiPolygon') &&
      Array.isArray(obj.coordinates)
    );
  }

  private buildTurfGeometry(
    raw: RawGeometry
  ): Feature<Polygon | MultiPolygon> | null {
    if (raw.type === 'Polygon') {
      return polygon(raw.coordinates as number[][][]);
    }
    if (raw.type === 'MultiPolygon') {
      return multiPolygon(raw.coordinates as number[][][][]);
    }
    return null;
  }

  /** Calcula bbox desde portales con margen de 500 m */
  private computeBbox(portales: Array<{ x: number; y: number }>): BBox | null {
    if (portales.length === 0) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of portales) {
      if (typeof p.x !== 'number' || typeof p.y !== 'number') continue;
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    }
    if (!isFinite(minX)) return null;
    const m = 500;
    return [minX - m, minY - m, maxX + m, maxY + m];
  }
}
